import type { Activity, DemoData, PulseItem, Task } from '../domain/types';
import { ymdIn } from '../lib/dates';
import { orderedSeries, type SeriesDef } from '../domain/series';

export const roleOf = (d: DemoData, activityId: string, personId: string = d.me): string | null =>
  d.activities[activityId]?.team.find((m) => m.ambassadorId === personId)?.role ?? null;

export const isAssigned = (d: DemoData, activityId: string, personId: string = d.me): boolean =>
  roleOf(d, activityId, personId) !== null;

export const tasksOf = (d: DemoData, activityId: string): Task[] => d.tasks.filter((t) => t.activityId === activityId);

export function progress(d: DemoData, activityId: string): { done: number; total: number; ratio: number } {
  const ts = tasksOf(d, activityId);
  const done = ts.filter((t) => t.done).length;
  return { done, total: ts.length, ratio: ts.length ? done / ts.length : 0 };
}

/** The earliest open task owned by the person on this activity. */
export function nextAction(d: DemoData, activityId: string, personId: string = d.me): Task | null {
  return tasksOf(d, activityId).filter((t) => t.ownerId === personId && !t.done).sort((a, b) => a.due.localeCompare(b.due))[0] ?? null;
}

/** Active activities the signed-in ambassador has a role on. Lead roles first, then by date. */
export function myActivities(d: DemoData): Activity[] {
  return Object.values(d.activities)
    .filter((a) => a.stage < 4 && isAssigned(d, a.id))
    .sort((a, b) => Number(b.organiserId === d.me) - Number(a.organiserId === d.me) || a.startsAt.localeCompare(b.startsAt));
}

/** Open tasks plus tasks completed for today or later, across all assigned activities. */
export function myTasks(d: DemoData, today: string, limit = 6): Task[] {
  return d.tasks
    .filter((t) => t.ownerId === d.me && d.activities[t.activityId] && (!t.done || t.due >= today))
    .sort((a, b) => a.due.localeCompare(b.due))
    .slice(0, limit);
}

/** Team-only items are visible only to members of that activity's team. */
export const canSee = (d: DemoData, item: PulseItem): boolean => item.visibility === 'network' || isAssigned(d, item.activityId);

/** Series that have at least one activity, in catalogue display order (for feed filters). */
export function seriesInUse(d: DemoData): SeriesDef[] {
  const used = new Set(Object.values(d.activities).map((a) => a.series));
  return orderedSeries(d.series).filter((s) => used.has(s.id));
}

export function visiblePulse(d: DemoData, filter: string): PulseItem[] {
  return d.pulse
    .filter((i) => d.activities[i.activityId] && canSee(d, i))
    .filter((i) => {
      const a = d.activities[i.activityId];
      if (filter === 'All') return true;
      if (filter === 'Mine') return isAssigned(d, a.id) || i.authorId === d.me;
      if (filter === 'Help wanted') return i.kind === 'help';
      return a.series === filter;
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export type PulseAction = 'open' | 'share' | 'follow' | 'help';

/** Which actions a Pulse card offers, first = primary. See MGL-HANDOVER §Pulse actions. */
export function pulseActions(d: DemoData, item: PulseItem): PulseAction[] {
  const a = d.activities[item.activityId];
  const role = roleOf(d, a.id);
  const kit = Boolean(a.shareKitId);
  if (role) {
    if (/promoter/i.test(role) && kit) return ['share', 'open'];
    return kit ? ['open', 'share'] : ['open'];
  }
  if (item.kind === 'help') return ['help', 'follow'];
  if (kit) return ['follow', 'share'];
  return a.needs.length ? ['follow', 'help'] : ['follow', 'open'];
}

export type CalView = 'Mine' | 'My UNITs' | 'Worldwide';
export type CalTone = 'lead' | 'role' | 'optional' | 'deadline';
export interface CalItem {
  key: string;
  ymd: string;
  kind: 'activity' | 'deadline';
  activityId: string;
  title: string;
  tag: { label: string; tone: CalTone };
}

/**
 * Calendar entries. Activity dates are the venue's local date.
 * Mine = assigned (responsibility) + followed (optional) + own deadlines.
 */
export function calendarItems(d: DemoData, view: CalView): CalItem[] {
  const me = d.ambassadors[d.me];
  const items: CalItem[] = [];
  for (const a of Object.values(d.activities)) {
    const role = roleOf(d, a.id);
    const followed = Boolean(d.followedActivities[a.id]);
    const inUnits = me.units.some((u) => u.name === a.city || u.name === a.country);
    const show = view === 'Worldwide' || (view === 'Mine' && (role !== null || followed)) || (view === 'My UNITs' && inUnits);
    if (!show) continue;
    const tag = role
      ? a.organiserId === d.me ? { label: 'YOU LEAD', tone: 'lead' as const } : { label: role.toUpperCase(), tone: 'role' as const }
      : { label: 'OPTIONAL', tone: 'optional' as const };
    items.push({ key: `a-${a.id}`, ymd: ymdIn(a.startsAt, a.timeZone), kind: 'activity', activityId: a.id, title: a.name, tag });
  }
  if (view === 'Mine') {
    for (const t of d.tasks) {
      if (t.ownerId === d.me && !t.done && d.activities[t.activityId]) {
        items.push({ key: `t-${t.id}`, ymd: t.due, kind: 'deadline', activityId: t.activityId, title: t.title, tag: { label: 'YOUR DEADLINE', tone: 'deadline' } });
      }
    }
  }
  return items.sort((x, y) => x.ymd.localeCompare(y.ymd) || Number(y.kind === 'deadline') - Number(x.kind === 'deadline'));
}

export function discoverActivities(d: DemoData, q: string, series: string, status: string): Activity[] {
  const s = q.trim().toLowerCase();
  return Object.values(d.activities)
    .filter((a) => series === 'All' || a.series === series)
    .filter((a) => status === 'Any status' || ['Idea', 'Planning', 'Scheduled', 'Live', 'Completed'][a.stage] === status)
    .filter((a) => !s || [a.name, a.city, a.country, a.type, d.ambassadors[a.organiserId]?.name ?? ''].join(' ').toLowerCase().includes(s))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}
